import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Download } from 'lucide-react'
import { AppHeader } from '#/components/layout/app-header'
import { Button } from '#/components/ui/button'
import { WorkspaceSimpleTabs } from '#/components/layout/workspace-simple-tabs'
import { PublishCard } from '#/components/publish/publish-card'
import { PublishChecklist } from '#/components/publish/checklist'
import { PublishVersions } from '#/components/publish/versions'
import { PwaGuide } from '#/components/publish/pwa-guide'
import { useChecklist, useDeploy, usePublish, useRollback, useSleepApp, useWakeApp } from '#/lib/api-publish'
import { ApiError } from '#/lib/api'
import { m } from '#/paraglide/messages'
import type { ChecklistDto } from '#/lib/publish/types'

/**
 * "Terbitkan" — the last screen of the simple journey (PRD §F2): one button, four green lines,
 * a version history to fall back on, and how to put the app on a phone's home screen.
 */
export const Route = createFileRoute('/workspace/$workspaceId/terbitkan')({
  component: TerbitkanView,
})

function TerbitkanView() {
  const { workspaceId } = Route.useParams()
  const { data, isLoading } = usePublish(workspaceId)
  const [checkNow, setCheckNow] = useState(false)
  const checklist = useChecklist(workspaceId, checkNow)
  const deploy = useDeploy(workspaceId)
  const rollback = useRollback(workspaceId)
  const sleep = useSleepApp(workspaceId)
  const wake = useWakeApp(workspaceId)
  const [notice, setNotice] = useState<string | null>(null)
  const [shown, setShown] = useState<ChecklistDto | null>(null)

  useEffect(() => {
    if (checklist.data) setShown(checklist.data)
  }, [checklist.data])

  const busy = deploy.isPending || rollback.isPending || sleep.isPending || wake.isPending
  const publish = () => {
    setNotice(null)
    deploy.mutate(false, {
      onSuccess: (r) => {
        if (r.checklist) setShown(r.checklist)
        if (r.blocked) setNotice(m.pb_blocked())
      },
      onError: (e) => setNotice(e instanceof ApiError ? e.message : m.pb_error_generic()),
    })
  }
  const recheck = () => {
    setCheckNow(true)
    void checklist.refetch()
  }

  return (
    <>
      <AppHeader showStats={false} compact right={<WorkspaceSimpleTabs workspaceId={workspaceId} active="terbitkan" />} />
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl space-y-4 p-4 pb-24 sm:p-6">
          <header>
            <h1 className="font-heading text-xl font-semibold">{m.pb_title()}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{m.pb_subtitle()}</p>
          </header>

          {isLoading && <p className="text-sm text-muted-foreground">{m.pb_check_checking()}</p>}
          {!isLoading && data && !data.buildable && <p className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">{m.pb_not_ready()}</p>}
          {notice && <p className="rounded-lg border border-status-blocked/40 bg-status-blocked/5 p-3 text-sm">{notice}</p>}

          {data?.buildable && (
            <>
              <PublishCard
                url={data.url}
                current={data.current}
                hostingUntil={data.hostingUntil}
                busy={busy}
                working={deploy.isPending || data.current?.status === 'building'}
                onPublish={publish}
                onSleep={() => sleep.mutate()}
                onWake={() => wake.mutate()}
              />

              <PublishChecklist data={shown} loading={checklist.isFetching} onRecheck={recheck} />

              <PublishVersions items={data.deployments} busyId={rollback.isPending ? rollback.variables : null} onRestore={(id) => rollback.mutate(id)} />

              <PwaGuide />

              <section className="rounded-xl border p-4 sm:p-5">
                <h2 className="text-base font-semibold">{m.pb_export_title()}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{m.pb_export_desc()}</p>
                <Button asChild variant="outline" size="sm" className="mt-3 gap-1.5">
                  <a href={`/api/workspace/${workspaceId}/app-export`}>
                    <Download className="size-4" /> {m.pb_export()}
                  </a>
                </Button>
              </section>
            </>
          )}
        </div>
      </main>
    </>
  )
}
